/* SEGMENT DEPENDENCIES */
import _get from 'lodash/get';
import Decimal from 'decimal.js';
import roundUpOrDown from '../../server/utils/roundUpOrDown';
import trackUserOptOut from '../marketplace/utils/segment/trackUserOptOut';
import determineLinkedCEO from '../marketplace/utils/segment/determineLinkedCEO';
import { NOT_SET } from '../marketplace/utils/segment/constants';
import checkSegmentCookie from '../marketplace/utils/segment/checkSegmentCookie';

import {
  floatingPointMultiplication,
  floatingPointSubtraction,
} from './mathUtil';
import { urlShopProduct } from '../../server/utils/urlConstants';

const dns = process.env.SERVER_DNS;

export { trackUserOptIn } from './trackingUtils/trackUserOptIn';
export { trackUserOptOut };

/*SEGMENT HELPER FUNCTIONS*/

export const determineShoppingMode = (user, cart, ceo) => {
  if (!user) return NOT_SET;
  const { conciergeCustomerAccountId, shoppingWithCeoUser, permissions } = user;
  const checkedCart = cart || user.cart;
  const isLoggedInAsCeo =
    permissions &&
    permissions.oneCommerce &&
    permissions.oneCommerce.ceo &&
    permissions.oneCommerce.ceo.access;
  switch (true) {
    case !!conciergeCustomerAccountId ||
      !!(ceo && ceo.conciergeCustomerAccountId):
      return 'Concierge';
    case !!shoppingWithCeoUser || (!!checkedCart.eventId && !isLoggedInAsCeo):
      return 'Self Service';
    default:
      return NOT_SET;
  }
};

export const determineOrderType = (user, eventId) => {
  if (!user) return NOT_SET;
  const { permissions, conciergeCustomerAccountId, shoppingWithCeoUser } = user;
  const isCeo =
    permissions &&
    permissions.oneCommerce &&
    permissions.oneCommerce.ceo &&
    permissions.oneCommerce.ceo.access;

  if (eventId) {
    return 'Party';
  }
  if (shoppingWithCeoUser) {
    return 'Storefront';
  }
  if (isCeo) {
    if (conciergeCustomerAccountId) {
      return 'Storefront';
    }
    return 'CEO Order';
  }
  return NOT_SET;
};

const formatTrackedProducts = user => {
  const { products } = user;
  const formattedProducts = products.edges.map(
    (
      {
        node: {
          content,
          company,
          images,
          inventoryQuantity,
          inventorySKU,
          name,
          price,
          productId,
          productURLslug,
          reviewAverage,
        },
      },
      index,
    ) => {
      const availability = inventoryQuantity > 0 ? 'In Stock' : 'Out of Stock';
      const product = {
        brand: company,
        imageUrl: `${urlShopProduct}${images.productCatalog}`,
        impactSmall: content.impactSmall,
        availability,
        sku: inventorySKU,
        name,
        position: index,
        price,
        productId,
        url: `${dns}/shop/product/${productURLslug}`,
        reviewAverage,
      };
      return product;
    },
  );
  return formattedProducts;
};

const formatProductViewed = (user, product, page, partyType, cart) => {
  const {
    specificCategory,
    category,
    company,
    productId,
    inventoryQuantity,
    inventorySKU,
    images,
    name,
    price,
    priceSale,
  } = product;
  const availability = inventoryQuantity > 0 ? 'In Stock' : 'Out of Stock';
  const productCatalog = images ? images.productCatalog : null;
  const priceBought = priceSale || price;
  const { eventId } = cart;
  const { shoppingWithCeoUser, conciergeCustomerAccountId, permissions } = user;
  const isCeo =
    permissions &&
    permissions.oneCommerce &&
    permissions.oneCommerce.ceo &&
    permissions.oneCommerce.ceo.access;
  const shoppingMode = determineShoppingMode(user);
  const linkedCeoId = determineLinkedCEO(user);
  const orderType = determineOrderType(user, eventId);
  const mappedPartyType =
    partyType === 'EPARTY'
      ? 'Virtual'
      : partyType === 'INHOME - SHIPTOGUEST'
      ? 'Home'
      : NOT_SET;
  return {
    availability,
    brand: company,
    orderChannel:
      shoppingWithCeoUser || conciergeCustomerAccountId || eventId || isCeo
        ? 'VIA'
        : NOT_SET,
    currency: 'USD',
    imageUrl: `${urlShopProduct}${productCatalog}`,
    category: specificCategory || category,
    label: name,
    linkedCeoId,
    name,
    orderType,
    page,
    partyId: eventId || NOT_SET,
    partyType: mappedPartyType,
    price: priceBought,
    productId,
    shoppingMode,
    sku: inventorySKU,
    url: window.location.href,
  };
};

const formatProductAdded = (user, product, cart, label, isRemove) => {
  const {
    productId,
    inventorySKU,
    name,
    price,
    priceSale,
    images,
    company,
  } = product;
  const productCatalogImage = images ? images.productCatalog : null;
  const priceBought = priceSale || price;
  const { eventId, qtyChange, event } = cart;
  const { shoppingWithCeoUser, conciergeCustomerAccountId, permissions } = user;
  const shoppingMode = determineShoppingMode(user);
  const linkedCeoId = determineLinkedCEO(user);
  const orderType = determineOrderType(user, eventId);
  const partyType =
    event && event.trinityPartyType === 'EPARTY'
      ? 'Virtual'
      : event && event.trinityPartyType === 'INHOME - SHIPTOGUEST'
      ? 'Home'
      : NOT_SET;
  const isCeo =
    permissions &&
    permissions.oneCommerce &&
    permissions.oneCommerce.ceo &&
    permissions.oneCommerce.ceo.access;
  return {
    brand: company,
    category: isRemove ? 'UX' : 'Add to Cart',
    orderChannel:
      shoppingWithCeoUser || conciergeCustomerAccountId || eventId || isCeo
        ? 'VIA'
        : NOT_SET, //not sure what we want default to be check with Mike
    imageUrl: `${urlShopProduct}${productCatalogImage}`,
    label,
    linkedCeoId, // if account is a ceo then do i put account id here?
    name,
    orderType,
    partyId: eventId || NOT_SET,
    partyType,
    price: priceBought,
    productId,
    quantity: qtyChange,
    quantityAddedToCart: qtyChange,
    shoppingMode,
    sku: inventorySKU,
    url: window.location.href,
  };
};

const handleShipping = (shippingPrice, shippingInfo, shippingCosts) => {
  let shipping;
  if (shippingInfo && shippingCosts) {
    const shippingMethod = shippingInfo ? shippingInfo.shippingMethod : null;
    shipping =
      shippingMethod === 'upsAir'
        ? shippingCosts.upsSecondDayAir
        : shippingCosts.upsGround;
  } else {
    shipping = shippingPrice || 0;
  }
  return shipping;
};

const cartCalculations = cart => {
  const {
    subTotalPriceOfItems,
    subTotalPriceOfItemsWithPromoCodes,
    taxTotal,
    promoCodes,
    promoCodeSavings,
    shippingPrice,
    shippingInfo,
    shippingCosts,
  } = cart;

  const shipping = handleShipping(shippingPrice, shippingInfo, shippingCosts);
  const subtotal = subTotalPriceOfItems;
  const whatWeCollected =
    promoCodeSavings.length > 0
      ? subTotalPriceOfItemsWithPromoCodes
      : subTotalPriceOfItems;
  const whatWeCollectedDecimal = new Decimal(whatWeCollected);
  const totalDecimal = whatWeCollectedDecimal.plus(taxTotal).plus(shipping);
  const revenue = roundUpOrDown(whatWeCollectedDecimal);
  const cartTotal = roundUpOrDown(totalDecimal);
  const coupon = promoCodes ? promoCodes[0].code : null;
  const discount = promoCodeSavings ? promoCodeSavings[0] : null;
  return {
    revenue,
    subtotal,
    total: cartTotal,
    coupon,
    discount,
    tax: taxTotal,
    shipping,
  };
};

const calculatePriceWithDiscount = (promo, priceBought, orderItemsLength) => {
  const {
    thresholds: { discountAmount },
  } = promo;
  if (discountAmount) {
    if (discountAmount.includes('%')) {
      const discountPercentage =
        1 - parseFloat(discountAmount.split('%')[0]) / 100;
      const price = floatingPointMultiplication(
        priceBought,
        discountPercentage,
      );
      return price;
    }
    const discountFlat = parseFloat(discountAmount) / orderItemsLength;
    const price = floatingPointSubtraction(priceBought, discountFlat);
    return price;
  }
  return priceBought;
};

const formatOrderProperties = (order, user, ceo) => {
  const {
    event,
    eventId,
    orderId,
    orderNumber,
    orderItems,
    createDate,
  } = order;
  const {
    shoppingWithCeoUser,
    conciergeCustomerAccountId,
    cart: { promoCodes },
    permissions,
  } = user;
  const filteredPromoCodes = promoCodes
    ? promoCodes.filter(code => !code.storeBalance)
    : null;
  const {
    coupon,
    discount,
    revenue,
    subtotal,
    total,
    shipping,
    tax,
  } = cartCalculations(user.cart);
  const shoppingMode = determineShoppingMode(user, null, ceo);
  const linkedCeoId = determineLinkedCEO(user);
  const formattedOrderItems = orderItems.map(
    ({ priceBought, quantity, product }) => {
      const price = filteredPromoCodes
        ? calculatePriceWithDiscount(
            filteredPromoCodes[0],
            priceBought,
            orderItems.length,
          )
        : priceBought;
      return {
        brand: product.company,
        category: product.category,
        name: product.name,
        imageURL: `${urlShopProduct}${product.images.productCatalog}`,
        price,
        productId: product.productId,
        quantity,
        sku: product.inventorySKU,
        url: `${dns}/shop/product/${product.productURLslug}`,
      };
    },
  );
  const isCeo =
    permissions &&
    permissions.oneCommerce &&
    permissions.oneCommerce.ceo &&
    permissions.oneCommerce.ceo.access;

  const orderType = determineOrderType(user, eventId);
  const partyType =
    event && event.trinityPartyType === 'EPARTY'
      ? 'Virtual'
      : event && event.trinityPartyType === 'INHOME - SHIPTOGUEST'
      ? 'Home'
      : NOT_SET;
  const formattedOrderProperties = {
    orderChannel:
      shoppingWithCeoUser || conciergeCustomerAccountId || eventId || isCeo
        ? 'VIA'
        : NOT_SET,
    coupon,
    currency: 'USD',
    discount,
    linkedCeoId,
    orderId,
    orderNumber,
    orderType,
    partyId: event ? event.eventId : NOT_SET, // prevent sending `nulls`
    partyType,
    subtotal,
    tax,
    total,
    orderTimestampUTC: createDate,
    products: formattedOrderItems,
    revenue,
    shipping,
    shoppingMode,
  };
  return formattedOrderProperties;
};

const formatInitiateCheckout = (user, cart) => {
  const { shoppingWithCeoUser, conciergeCustomerAccountId, permissions } = user;
  const shoppingMode = determineShoppingMode(user);
  const linkedCeoId = determineLinkedCEO(user);
  const isCeo =
    permissions &&
    permissions.oneCommerce &&
    permissions.oneCommerce.ceo &&
    permissions.oneCommerce.ceo.access;
  const products = cart.entries.edges.map(({ node: { product, quantity } }) => {
    const {
      category,
      company,
      images,
      inventorySKU,
      name,
      price,
      productId,
      productURLslug,
    } = product;

    return {
      category,
      brand: company,
      imageURL:
        images && images.productCatalog && urlShopProduct
          ? `${urlShopProduct}${images.productCatalog}`
          : '',
      sku: inventorySKU,
      name,
      price,
      productId,
      url: `${dns}/shop/product/${productURLslug}`,
      quantityInCart: quantity,
      quantity,
    };
  });
  const {
    coupon,
    discount,
    revenue,
    subtotal,
    total,
    shipping,
    tax,
  } = cartCalculations(cart);
  const orderType = determineOrderType(user, cart.eventId);
  const partyType =
    cart.event && cart.event.trinityPartyType === 'EPARTY'
      ? 'Virtual'
      : cart.event && cart.event.trinityPartyType === 'INHOME - SHIPTOGUEST'
      ? 'Home'
      : NOT_SET;
  return {
    // value: cart.subTotalPriceOfItemsWithPromoCodes || cart.subTotalPriceOfItems,
    revenue,
    subtotal,
    shipping,
    tax,
    total,
    discount,
    coupon,
    currency: 'USD',
    linkedCeoId,
    orderType,
    orderChannel:
      shoppingWithCeoUser || conciergeCustomerAccountId || cart.eventId || isCeo
        ? 'VIA'
        : NOT_SET,
    partyId: cart.eventId || NOT_SET,
    partyType,
    products,
    shoppingMode,
  };
};

const formatProductReview = (product, review) => {
  const {
    company,
    category,
    images,
    inventoryQuantity,
    inventorySKU,
    name,
    price,
    priceSale,
    productId,
    productURLslug,
  } = product;
  const { comment, date, rating, reviewId, title } = review;

  const availability = inventoryQuantity > 0 ? 'In Stock' : 'Out of Stock';
  return {
    application: 'One Commerce',
    product: {
      availability,
      brand: company,
      category,
      imageUrl: `${urlShopProduct}${images.productCatalog}`,
      name,
      price: priceSale || price,
      productId,
      sku: inventorySKU,
      url: `${dns}/shop/product/${productURLslug}`,
    },
    rating,
    reviewId,
    reviewBody: comment,
    reviewTimestampUTC: date,
    title,
  };
};

const handlePaymentMethod = (paymentInfo, totalMinusSavings) => {
  switch (true) {
    case totalMinusSavings === 0:
      return 'Gift Card';
    case paymentInfo && paymentInfo.provider === 'square':
      return 'Square';
    case !!paymentInfo:
      return paymentInfo.brand;
    default:
      return NOT_SET;
  }
};

const formatPaymentInfoEntered = (user, cart, ceo) => {
  const { eventId, paymentInfo, totalMinusSavings } = cart;
  const { shoppingWithCeoUser, conciergeCustomerAccountId, permissions } = user;
  const shoppingMode = determineShoppingMode(user, cart, ceo); // this prop is wrong bc cart/user becomes customer in checkout
  const linkedCeoId = determineLinkedCEO(user);
  const orderType = determineOrderType(user, eventId);
  const isCeo =
    permissions &&
    permissions.oneCommerce &&
    permissions.oneCommerce.ceo &&
    permissions.oneCommerce.ceo.access;
  const paymentMethod = handlePaymentMethod(paymentInfo, totalMinusSavings);
  const partyType =
    cart.event && cart.event.trinityPartyType === 'EPARTY'
      ? 'Virtual'
      : cart.event && cart.event.trinityPartyType === 'INHOME - SHIPTOGUEST'
      ? 'Home'
      : NOT_SET;
  return {
    orderChannel:
      shoppingWithCeoUser || conciergeCustomerAccountId || cart.eventId || isCeo
        ? 'VIA'
        : NOT_SET,
    linkedCeoId,
    orderType,
    partyId: cart.eventId || NOT_SET,
    partyType,
    paymentMethod,
    shoppingMode,
    step: 6,
  };
};

const formatProductsSearched = (user, query, itemClicked) => {
  const { eventId, event } = user.cart;
  const { shoppingWithCeoUser, conciergeCustomerAccountId, permissions } = user;
  const shoppingMode = determineShoppingMode(user);
  const linkedCeoId = determineLinkedCEO(user);
  const orderType = determineOrderType(user, eventId);
  const partyType =
    event && event.trinityPartyType === 'EPARTY'
      ? 'Virtual'
      : event && event.trinityPartyType === 'INHOME - SHIPTOGUEST'
      ? 'Home'
      : NOT_SET;
  const isCeo =
    permissions &&
    permissions.oneCommerce &&
    permissions.oneCommerce.ceo &&
    permissions.oneCommerce.ceo.access;
  return {
    orderChannel:
      shoppingWithCeoUser || conciergeCustomerAccountId || eventId || isCeo
        ? 'VIA'
        : NOT_SET,
    itemClicked,
    linkedCeoId,
    orderType,
    partyId: eventId || NOT_SET,
    partyType,
    query,
    shoppingMode,
  };
};

const formatConciergeShoppingStarted = (cart, user) => {
  const event =
    cart && cart.event
      ? cart.event
      : {
          eventId: NOT_SET,
          trinityPartyType: NOT_SET,
          trinityPartyId: NOT_SET,
        };

  const orderType = event.eventId ? 'Party' : 'Storefront';
  const partyType =
    event && event.trinityPartyType === 'EPARTY'
      ? 'Virtual'
      : event && event.trinityPartyType === 'INHOME - SHIPTOGUEST'
      ? 'Home'
      : NOT_SET;

  const partyId = event.eventId;
  const linkedCeoId = user.userId;

  return {
    orderChannel: 'VIA', // must be VIA
    shoppingMode: 'Concierge',
    linkedCeoId,
    orderType,
    partyId,
    partyType,
  };
};

/* PIXLEE TRACKING FUNCTIONS */

const pixleeAddToCart = (product, cart) => {
  if (window.pixlee_analytics) {
    const { inventorySKU, name, price, priceSale } = product;
    pixlee_analytics.events.trigger('add:to:cart', {
      product_sku: inventorySKU,
      quantity: cart.qtyChange,
      name,
      price: priceSale || price,
    });
  }
};

const pixleeOrderCompleted = (order, user) => {
  const { orderId, orderItems } = order;
  const { total, totalNumberOfItems } = cartCalculations(user.cart);
  const formattedOrderItems = orderItems.map(
    ({ priceBought, quantity, product }) => ({
      price: priceBought,
      quantity,
      sku: product.inventorySKU,
    }),
  );
  if (window.pixlee_analytics) {
    window.pixlee_analytics.events.trigger('converted:photo', {
      cart_contents: formattedOrderItems,
      cart_total: total,
      cart_total_quantity: totalNumberOfItems,
      order_id: orderId,
    });
  }
};

/* SEGMENT TRACKING FUNCTIONS */

export const resetSegment = () => {
  if (process.env.ENABLE_SEGMENT === 'true') {
    if (typeof window !== 'undefined' && typeof analytics !== 'undefined') {
      if (analytics._user) {
        analytics.reset();
        analytics._user.id(null);
      }
    }
  }
};

export const trackEvent = eventParams => {
  if (typeof window !== 'undefined' && typeof analytics !== 'undefined') {
    // userId must be passesd in along with name of event
    const { user, event, properties, reset } = eventParams;
    checkSegmentCookie(user);
    const integrations = {
      Klaviyo: {
        confirmOptin: false,
        listId: 'do not add',
      },
      Intercom: { hideDefaultLauncher: true },
    };
    let trackProperties;
    if (process.env.ENABLE_SEGMENT) {
      switch (true) {
        case event === 'Checkout Started':
          trackProperties = formatInitiateCheckout(user, properties.cart);
          break;
        case event === 'Completed Account Registration':
          if (user.firstName && user.lastName) {
            trackProperties = {
              firstName: user.firstName,
              lastName: user.lastName,
            };
          }
          break;
        case event === 'Payment Info Entered':
          trackProperties = formatPaymentInfoEntered(
            user,
            properties.cart,
            properties.ceo,
          );
          break;
        case event === 'Order Completed': {
          pixleeOrderCompleted(properties.order, user);
          trackProperties = formatOrderProperties(
            properties.order,
            user,
            properties.ceo,
          );
          break;
        }
        case event === 'Product Removed': {
          trackProperties = formatProductAdded(
            user,
            properties.product,
            properties.cart,
            properties.label,
            true,
          );
          break;
        }
        case event === 'Product Added': {
          const { cart, product, label } = properties;
          pixleeAddToCart(product, cart);
          trackProperties = formatProductAdded(user, product, cart, label);
          break;
        }
        case event === 'Product List Viewed':
          trackProperties = {
            category: properties.category,
            products: formatTrackedProducts(user),
          };
          integrations.All = false;
          break;
        case event === 'Product Viewed':
          trackProperties = formatProductViewed(
            user,
            properties.product,
            properties.page,
            properties.partyType,
            properties.cart,
          );
          break;
        case event === 'Product Reviewed':
          trackProperties = formatProductReview(
            properties.product,
            properties.review,
          );
          break;
        case event === 'Products Searched':
          trackProperties = formatProductsSearched(
            user,
            properties.query,
            properties.itemClicked,
          );
          break;
        case event === 'Concierge Shopping Started':
          trackProperties = formatConciergeShoppingStarted(
            properties.cart,
            user,
          );
          break;
        case event === 'CEO Lead Form Viewed':
          trackProperties = {
            application: 'One Commerce',
            formName: 'CEO Lead Capture',
            url: 'https://www.onehopewine.com/join',
            userId: user.email,
          };
          break;
        case event === 'Corporate Gifting Form Viewed':
          trackProperties = {
            application: 'One Commerce',
            formName: 'Corporate Gifting Lead Capture',
            url: 'https://www.onehopewine.com/corporate',
            userId: user.email,
          };
          break;
        case event === 'Host Lead Form Viewed':
          trackProperties = {
            application: 'One Commerce',
            formName: 'Host Lead Capture',
            url: 'https://www.onehopewine.com/host',
            userId: user.email,
          };
          break;
        case event === 'Email Subscription Sign-Up':
          trackProperties = {
            application: 'One Commerce',
            location: 'Footer',
            userId: user.email,
            email: user.email,
          };
          break;
        case event.includes('Form Conversion'):
        case event === 'User Login':
        case event === 'User Logout':
        case event === 'Promotion Clicked':
        default:
          trackProperties = properties;
          break;
      }
      trackProperties.application = 'One Commerce';

      if (user) {
        const { shoppingWithCeoUser, conciergeUser } = user;
        const isShoppingWithAnother = conciergeUser || shoppingWithCeoUser;
        if (
          isShoppingWithAnother &&
          isShoppingWithAnother.email &&
          isShoppingWithAnother.userId
        ) {
          trackProperties.customerAccountId = isShoppingWithAnother.userId;
          trackProperties.customerEmail = isShoppingWithAnother.email;
        }
      }
      if (reset) {
        return analytics.track(event, trackProperties, { integrations }, () =>
          resetSegment(),
        );
      }
      return analytics.track(event, trackProperties, { integrations });
    }
  }
};

export const trackPageView = (user, name, properties, permissions) => {
  if (typeof window !== 'undefined' && typeof analytics !== 'undefined') {
    checkSegmentCookie(user);
    const ceoAccess = _get(permissions, ['oneCommerce', 'ceo', 'access']);
    const enableIntercom =
      permissions && ceoAccess && properties.url.includes('/ceo');
    if (process.env.ENABLE_SEGMENT) {
      const pageParams = { ...properties, application: 'One Commerce' };
      const integrations = {
        All: true,
        Klaviyo: {
          confirmOptin: false,
          listId: 'do not add',
        },
        Intercom: { hideDefaultLauncher: !enableIntercom },
      };

      if (user) {
        const { shoppingWithCeoUser, conciergeUser } = user;
        const isShoppingWithAnother = conciergeUser || shoppingWithCeoUser;
        if (
          isShoppingWithAnother &&
          isShoppingWithAnother.email &&
          isShoppingWithAnother.userId
        ) {
          pageParams.customerAccountId = isShoppingWithAnother.userId;
          pageParams.customerEmail = isShoppingWithAnother.email;
        }
      }
      const { userId, email } = user;
      if (userId && userId.indexOf('.') === -1) {
        analytics.identify(userId.toString(), {
          email,
        });
      }

      return analytics.page(null, name, pageParams, integrations);
    }
  }
};

const noop = () => {};
//const FBQ = fbq;
const FS_HC = window.FS_HC || null;
const GOOGLE_TRACK_CONVERSION = window.google_trackConversion || noop;
const BING_UETQ = window.uetq || [];

/* FACEBOOK PIXEL EVENT TRACKING FUNCTIONS */

export const trackLoginRegister = (publicId, email) => {
  if (FS_HC) {
    FS_HC.identify(publicId, {
      email,
    });
  }
};

export const trackBingOrder = order => {
  BING_UETQ.push({
    ec: 'purchase',
    ea: 'click',
    gv: order.orderTotal,
    gc: 'USD',
  });
};
